/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormState } from '../../../container/forms/types/core/form-state.types';
import { PaymentWorkflowType } from '../../../container/forms/types/api/graph.types';
import { PaymentRow } from '../../../../components/container/forms/schemas';
import { getValidatedFieldValue, pickFields, isMeaningful } from './utils';
import {
  buildOfficeAccClientMatterApiFields,
  buildDebitCreditClientMatterApiFields,
  resolveTransferRequisitionNameForApi,
} from './fieldMapping';
import { normalizeBankingFormData } from '../../../container/forms/schemas/Utils/bankingValidation';
import {
  buildOvertimeWorkedSubmitGrid,
  getLineManagerMailForSubmit,
} from '../../../container/forms/schemas/OvertimeApproval/utils';
import { buildGiftHospitalityDonationSectionSubmitPayload } from './giftHospitalityDonationMapper';
// PLOP_INJECT_FORM_TO_WORKFLOW_IMPORTS

/** Result for one section when mapping form state → API submit payload. */
export interface SectionSubmitPayload {
  fields: Record<string, any>;
  gridData: Record<string, any>;
  documents: Record<string, any>;
}

/** @deprecated Use SectionSubmitPayload */
export type CurrentSectionPayload = SectionSubmitPayload;

/**
 * Build the current section's fields, gridData, and documents for API submit.
 * Dispatches by workflow type and currentSection.
 */
export const buildSectionSubmitPayload = (
  formState: FormState,
): SectionSubmitPayload => {
  const workflowType = formState.workflowType || PaymentWorkflowType.Undefined;
  const currentSection = formState.currentSection;
  const data = formState.data;

  const base = {
    fields: {} as Record<string, any>,
    gridData: {} as Record<string, any>,
    documents: {} as Record<string, any>,
  };

  switch (workflowType) {
    case PaymentWorkflowType.DubaiPaymentApproval:
    case PaymentWorkflowType.Undefined:
      return buildDubaiPaymentApprovalSection(currentSection, data, base);
    case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
      return buildDubaiPaymentFromOfficeAccSection(currentSection, data, base);
    case PaymentWorkflowType.DubaiTransferRequisition:
      return buildDubaiTransferRequisitionSection(currentSection, data, base);
    case PaymentWorkflowType.OvertimeApproval:
      return buildOvertimeApprovalSection(currentSection, data, base);
    case PaymentWorkflowType.GiftHospitalityDonationUk:
    case PaymentWorkflowType.GiftHospitalityDonationDubai:
    case PaymentWorkflowType.GiftHospitalityDonationDublin:
      return buildGiftHospitalityDonationSectionSubmitPayload(
        currentSection,
        data
      );
// PLOP_INJECT_FORM_TO_WORKFLOW_CASE
    default:
      return base;
  }
};

/** @deprecated Use buildSectionSubmitPayload */
export const buildCurrentSectionPayload = buildSectionSubmitPayload;

const buildDubaiPaymentApprovalSection = (
  sectionKey: string,
  data: FormState['data'],
  base: SectionSubmitPayload,
): SectionSubmitPayload => {
  switch (sectionKey) {
    case 'payment_details': {
      const { clientName, clientCode, matterName, matterCode } =
        parseClientMatterFromData(data);
      base.documents = {
        payment_spreadsheet: data.payment_spreadsheet || null,
        payment_attachment: data.payment_attachment || null,
      };
      base.fields = {
        client: clientName,
        client_code: clientCode,
        matter: matterName,
        matter_code: matterCode,
        interest: data.interest,
        interest_options: data.interest_options,
        payments_count: data.payments_count,
        payment_total: data.payment_total?.toString() || '',
        comments: data.comments,
        responsible_lawyer: getValidatedFieldValue(data.responsible_lawyer),
        first_partner: getValidatedFieldValue(data.first_partner),
        payment_hold_review: data.hold_review_comments,
        location: data.location || 'nonUk',
      };
      base.fields.second_partner = resolveSecondPartnerValue(data);
      base.gridData = buildPaymentDetailsGridData(data);
      break;
    }
    case 'resp_lawyer':
      base.documents = { resp_attachment: data.resp_attachment || null };
      base.fields = {
        client: data.client_name || data.client,
        matter: data.matter_name || data.matter,
        resp_approve: data.resp_approve,
        resp_reject_details: data.resp_reject_details,
        resp_verify_details: data.resp_verify_details,
        resp_verify_bank: data.resp_verify_bank,
        resp_bank_confirmed_by: getValidatedFieldValue(
          data.resp_bank_confirmed_by,
        ),
        resp_verify_currency: data.resp_verify_currency,
        resp_hold_payment: data.resp_hold_payment,
        resp_comments: data.resp_comments,
      };
      break;
    case 'first_partner':
      base.fields = {
        client: data.client_name || data.client,
        matter: data.matter_name || data.matter,
        first_approver: data.first_approver,
        first_reject_details: data.first_reject_details,
        first_verify_details: data.first_verify_details,
        first_verify_bank: data.first_verify_bank,
        first_verify_currency: data.first_verify_currency,
        first_verify_reason: data.first_verify_reason,
        first_comments: data.first_comments,
      };
      break;
    case 'second_partner':
      base.fields = {
        client: data.client_name || data.client,
        matter: data.matter_name || data.matter,
        second_approver: data.second_approver,
        second_reject_details: data.second_reject_details,
        second_verify_details: data.second_verify_details,
        second_verify_bank: data.second_verify_bank,
        second_verify_currency: data.second_verify_currency,
        second_verify_reason: data.second_verify_reason,
        second_comments: data.second_comments,
      };
      break;
    case 'payment_hold_review':
      base.fields = { hold_review_comments: data.hold_review_comments };
      break;
    case 'payment_confirmation':
      base.documents = {
        payment_upload_documentation: data.payment_upload_documentation || null,
      };
      base.fields = {
        client: data.client_name || data.client,
        matter: data.matter_name || data.matter,
        payment_confirmation: data.payment_confirmation,
        payment_rejection_details: data.payment_rejection_details,
        payment_comments: data.payment_comments,
        payment_total_interest: data.payment_total_interest,
      };
      break;
    case 'document_saving':
      base.fields = {
        client: data.client_name || data.client,
        matter: data.matter_name || data.matter,
        documents_saved: data.documents_saved,
        form_saved: data.form_saved,
      };
      break;
  }
  return base;
};

const parseClientMatterFromData = (
  data: FormState['data'],
): {
  clientName: string | undefined;
  clientCode: string | undefined;
  matterName: string | undefined;
  matterCode: string | undefined;
} => {
  let clientName = data.client_name || data.client;
  let clientCode = data.client_code;
  const clientMatch = clientName?.match(/(.*?)\s*-\s*\((.*)\)/);
  if (clientMatch && clientMatch.length >= 3) {
    clientName = clientMatch[1].trim();
    clientCode = clientMatch[2].trim();
  }
  let matterName = data.matter_name || data.matter;
  let matterCode = data.matter_code;
  const matterMatch = matterName?.match(/(.*?)\s*-\s*\((.*)\)/);
  if (matterMatch && matterMatch.length >= 3) {
    matterName = matterMatch[1].trim();
    matterCode = matterMatch[2].trim();
  }
  return { clientName, clientCode, matterName, matterCode };
};

const getObjectValue = (val: unknown): string | undefined => {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    const maybe = val as { value?: unknown };
    return typeof maybe.value === 'string' && maybe.value.trim() !== ''
      ? maybe.value.trim()
      : undefined;
  }
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0] as unknown;
    if (first && typeof first === 'object') {
      const maybe = first as { value?: unknown };
      return typeof maybe.value === 'string' && maybe.value.trim() !== ''
        ? maybe.value.trim()
        : undefined;
    }
  }
  return undefined;
};

const getPlainString = (val: unknown): string | undefined => {
  return typeof val === 'string' && val.trim() !== '' ? val.trim() : undefined;
};

const getPaymentTotalFromFormData = (data: FormState['data']): number => {
  const directPaymentTotal = parseFloat(String(data.payment_total)) || 0;
  if (directPaymentTotal > 0) {
    return directPaymentTotal;
  }

  const gridData = data.gridData;
  const rowsToProcess = Array.isArray(gridData)
    ? gridData
    : Array.isArray((gridData as { data?: PaymentRow[] })?.data)
      ? (gridData as { data: PaymentRow[] }).data
      : [];

  return rowsToProcess.reduce((total, row) => {
    return total + (parseFloat(String(row.amount)) || 0);
  }, 0);
};

const extractSecondPartnerFieldValue = (val: unknown): string | undefined => {
  const objectValue = getObjectValue(val);
  if (objectValue !== undefined) {
    return objectValue;
  }
  return getPlainString(val);
};

/** Resolve second partner for API — uses payment-total field, not stale mirrored copies. */
const resolveSecondPartnerValue = (data: FormState['data']): string => {
  const paymentTotal = getPaymentTotalFromFormData(data);
  const primaryField =
    paymentTotal >= 25000
      ? 'second_partner_above_25k'
      : 'second_partner_below_25k';

  const primaryValue = extractSecondPartnerFieldValue(data[primaryField]);
  if (primaryValue !== undefined) {
    return primaryValue;
  }

  const canonicalValue = extractSecondPartnerFieldValue(data.second_partner);
  if (canonicalValue !== undefined) {
    return canonicalValue;
  }

  return '';
};

const buildPaymentDetailsGridData = (
  data: FormState['data'],
): Record<string, any> => {
  const gridData = data.gridData;
  if (!gridData) return {};
  const rows = Array.isArray(gridData)
    ? gridData
    : (gridData as { data?: any[] })?.data;
  if (!Array.isArray(rows) || rows.length === 0) return {};
  return {
    payment_details: rows.map((payment: PaymentRow) => {
      const normalizedPayment = normalizeBankingFormData(
        payment as unknown as Record<string, unknown>,
      );

      return {
        location: payment.location || '',
        payee: payment.payee || '',
        currency: payment.currency || '',
        other_currency: payment.currencyCustom || '',
        amount: payment.amount?.toString() || '',
        account_number: normalizedPayment.accno || '',
        sort_code: normalizedPayment.sortCode || '',
        iban: normalizedPayment.iban || '',
        swift_bic: normalizedPayment.swiftBicCode || '',
        bank_name: payment.bankName || '',
        bank_address: payment.bankAddress || '',
        type: payment.type || '',
        purpose: payment.purpose || '',
        other_purpose: payment.purposeCustom || '',
        deduct_charges: payment.deductBankCharges || '',
        reference: payment.reference?.toString() || '',
        ledger_desc: payment.ledgerDesc?.toString() || '',
      };
    }),
  };
};

const buildDubaiPaymentFromOfficeAccSection = (
  sectionKey: string,
  data: FormState['data'],
  base: SectionSubmitPayload,
): SectionSubmitPayload => {
  switch (sectionKey) {
    case 'dubai_payment_from_office_account_payment_details': {
      // Banking fields (account/sort/iban/bic) are normalized before sending.
      const normalizedData = normalizeBankingFormData(data);
      // Always send invoice_upload so cleared/removed files are deleted server-side.
      // When VAT invoice is "no", force empty string regardless of leftover form data.
      const invoiceUploadValue = data.invoiceUpload ?? data.invoice_upload;
      base.documents = {
        invoice_upload:
          normalizedData.vatInvoiceAttached === 'no'
            ? ''
            : invoiceUploadValue || '',
        attachments: data.attachments || null,
      };
      base.fields = {
        reference_number: normalizedData.referenceNumber,
        budget_name: normalizedData.budgetName,
        budget_code: normalizedData.budgetCode,
        payment_type: normalizedData.paymentType,
        date: normalizedData.date,
        amount: normalizedData.amount,
        to_whom_payable: normalizedData.toWhomPayable,
        payment_reference: normalizedData.paymentReference,
        ledger_description: normalizedData.ledgerDescription,
        receiving_bank_name: normalizedData.receivingBankName,
        bank_address: normalizedData.bankAddress,
        account_no: normalizedData.accountNo,
        sort_code: normalizedData.sortCode,
        iban: normalizedData.iban,
        bic: normalizedData.bic,
        vat_invoice_attached: normalizedData.vatInvoiceAttached,
        // Client/matter names, codes and unos (added so the API can persist/return them)
        ...buildOfficeAccClientMatterApiFields(data),
        // Approver tag picker → single email value
        approver: getValidatedFieldValue(data.approver),
        location: normalizedData.location || 'nonUk',
      };
      break;
    }
    case 'dubai_payment_from_office_account_approver': {
      const authApprove = data.approverAuthApprove;
      base.fields = {
        approver_auth_approve: authApprove,
        approver_approval_details:
          authApprove === 'yes'
            ? data.approverApprovalDetails || ''
            : authApprove === 'no'
              ? data.approverRejectionDetails || ''
              : '',
      };
      break;
    }
    case 'dubai_payment_from_office_account_payment_confirmation': {
      base.documents = {
        payment_upload_documentation:
          data.paymentFromOfficePaymentUploadDocumentation || null,
      };
      base.fields = pickFields({
        payment_from_office_payment_confirmation:
          data.paymentFromOfficePaymentConfirmation,
        payment_from_office_payment_rejection_details:
          data.paymentFromOfficePaymentRejectionDetails,
        payment_from_office_payment_comments:
          data.paymentFromOfficePaymentComments,
      });
      break;
    }
  }
  return base;
};

const buildDubaiTransferRequisitionSection = (
  sectionKey: string,
  data: FormState['data'],
  base: SectionSubmitPayload,
): SectionSubmitPayload => {
  switch (sectionKey) {
    case 'dubai_transfer_requisition_payment_details': {
      const debitCreditClient = data.debitType === 'client' && data.creditType === 'client';
      base.fields = {
        debit_type: data.debitType,
        credit_type: data.creditType,
        debit_client_name: resolveTransferRequisitionNameForApi(
          data.debitClientName,
          data.debitSelectedClientLabel,
        ),
        debit_matter_name: resolveTransferRequisitionNameForApi(
          data.debitMatterName,
          data.debitSelectedMatterLabel,
        ),
        debit_date: data.debitDate,
        debit_amount: data.debitAmount,
        debit_description: data.debitDescription,
        credit_client_name: data.creditClientName,
        credit_matter_name: data.creditMatterName,
        debit_bill_number: data.debitBillNumber,
        credit_bill_number: data.creditBillNumber,
        debit_general_ledger_codes: data.debitGeneralLedgerCodes,
        credit_general_ledger_codes: data.creditGeneralLedgerCodes,
        // Debit/credit client/matter codes (or combined number) split for the API
        ...buildDebitCreditClientMatterApiFields(data),
        // Partner/fee-earner tag pickers → single email values
        first_partner: getValidatedFieldValue(data.firstPartner),
        fee_earner: getValidatedFieldValue(data.feeEarner),
        // Second partner only applies when both sides are client accounts
        second_partner: debitCreditClient
          ? getValidatedFieldValue(data.secondPartner) || ''
          : '',
        location: data.location || 'nonUk',
      };
      break;
    }
    case 'dubai_transfer_requisition_first_partner': {
      base.fields = {
        ...pickFields({
          first_partner_approval: data.firstPartnerApproval,
          first_partner_auth_approve_comments:
            data.firstPartnerAuthApproveComments,
        }),
        ...pickFields(
          { first_partner_rejection_details: data.firstPartnerRejectionDetails },
          isMeaningful,
        ),
      };
      break;
    }
    case 'dubai_transfer_requisition_second_partner': {
      base.fields = {
        ...pickFields({
          second_partner_auth_approve: data.secondPartnerAuthApprove,
          second_partner_auth_approve_comments:
            data.secondPartnerAuthApproveComments,
          second_partner_approval: data.secondPartnerApproval,
        }),
        ...pickFields(
          {
            second_partner_rejection_details:
              data.secondPartnerRejectionDetails,
          },
          isMeaningful,
        ),
      };
      break;
    }
    case 'dubai_transfer_requisition_payment_confirmation':
      base.documents = {
        transfer_requisition_payment_upload_documentation:
          data.transfer_requisition_payment_upload_documentation || null,
      };
      base.fields = pickFields({
        transfer_requisition_payment_confirmation:
          data.transfer_requisition_payment_confirmation,
        transfer_requisition_payment_rejection_details:
          data.transfer_requisition_payment_rejection_details,
        transfer_requisition_payment_comments:
          data.transfer_requisition_payment_comments,
        transfer_requisition_payment_total_interest:
          data.transfer_requisition_payment_total_interest,
      });
      break;
  }
  return base;
};

const buildOvertimeApprovalSection = (
  sectionKey: string,
  data: FormState['data'],
  base: SectionSubmitPayload,
): SectionSubmitPayload => {
  switch (sectionKey) {
    case 'overtime_approval_request_details': {
      base.fields = pickFields({
        employee_name: data.employee_name,
        job_title: data.job_title,
        department: data.department,
        role_type: data.role_type,
        employee_number: data.employee_number,
        standard_working_hours: data.standard_working_hours,
        line_manager: getLineManagerMailForSubmit(data.line_manager),
        overtime_policy: data.overtime_policy,
      });
      base.gridData = buildOvertimeWorkedSubmitGrid(data.overtime_worked);
      break;
    }
    case 'overtime_approval_line_manager':
      base.fields = pickFields({
        confirm_approval_overtime_request:
          data.confirm_approval_overtime_request,
        line_manager_comments: data.line_manager_comments,
      });
      break;
    case 'overtime_approval_finance_confirmation':
      base.fields = pickFields({
        confirmation: data.confirmation,
      });
      break;
  }
  return base;
};
